import type { QualifiedRelation } from './dependencyGraph';

/**
 * Hand-rolled React Query keys and the relations they read.
 *
 * Queries created through `@supabase-cache-helpers/postgrest-react-query` encode their
 * schema/relation into the cache key, so live-sync can match them automatically. Queries
 * written with plain `useQuery` (workspace pages, RPC-backed boards, admin panels) cannot —
 * they are declared here instead.
 *
 * `relations` may name a view; the dependency graph resolves a base-table change to its
 * views, so declaring the relation the query actually reads is enough.
 *
 * `key` is matched as a prefix, so `['funder-target-submissions']` covers
 * `['funder-target-submissions', 42]`.
 */
export interface CustomQueryKeySource {
  key: readonly (string | number)[];
  relations: QualifiedRelation[];
  /** Match only keys at most this long, so a frequent change skips the costlier queries nested under the prefix. */
  maxLength?: number;
}

export const CUSTOM_QUERY_KEY_SOURCES: CustomQueryKeySource[] = [
  // Board pages backed by RPCs (dependencies cannot be derived from pg_depend)
  {
    key: ['opportunities-board'],
    relations: ['public.submission', 'public.merchant', 'public.rep']
  },
  { key: ['submission'], relations: ['public.submission'] },

  // REG-828: admin default grid columns (useGridColumnDefaults)
  { key: ['grid-column-defaults'], relations: ['core_crm.grid_column_default'] },

  // Custom pages backed by generated views
  {
    key: ['funder-target-page'],
    relations: ['public.funder_deep_view', 'public.submission_view']
  },
  { key: ['funder-target-submissions'], relations: ['public.submission_view'] },
  {
    key: ['rep-performance-page'],
    relations: ['public.rep_deep_view', 'public.rep_performance_summary_v']
  },
  { key: ['rep-performance-submissions'], relations: ['public.rep_submission_activity_v'] },
  { key: ['lead-source-workspace'], relations: ['public.lead_source_view'] },
  { key: ['lead-source-submissions'], relations: ['public.lead_source_submission_activity_v'] },

  // Imports page
  // Lead import workspace. Only the one-row-per-action tables are replicated;
  // bulk rows (leads, observations, results) roll up into the batch or run row,
  // so a change there refreshes the views that read them.
  { key: ['lead-import', 'batches'], relations: ['public.lead_import', 'public.lead_import_job'] },
  {
    key: ['lead-import', 'batch'],
    relations: ['public.lead_import', 'public.lead_import_sheet', 'public.lead_purchase']
  },
  // A verify job writes its row dozens of times a minute; that refreshes the
  // batch status (['lead-import', 'batch', id]) only, not the summary and
  // duplicate reports under it, which no job changes (REG-888).
  { key: ['lead-import', 'batch'], relations: ['public.lead_import_job'], maxLength: 3 },
  ...['leads', 'lead', 'merchant'].map((kind) => ({
    key: ['lead-import', kind],
    relations: [
      'public.lead_import',
      'public.lead_assignment',
      'public.lead_redistribution',
      'public.lead_verification_run',
      'public.lead_promotion',
      'public.merchant'
    ]
  })),
  { key: ['lead-import', 'holds'], relations: ['public.lead_import', 'public.lead_promotion'] },
  { key: ['lead-import', 'assignments'], relations: ['public.lead_assignment'] },
  { key: ['lead-import', 'assignment'], relations: ['public.lead_assignment'] },
  { key: ['lead-import', 'redistribution'], relations: ['public.lead_redistribution', 'public.lead_assignment'] },
  { key: ['lead-import', 'verification-run'], relations: ['public.lead_verification_run'] },
  { key: ['lead-import', 'purchases'], relations: ['public.lead_purchase', 'public.lead_import'] },
  { key: ['lead-import', 'profiles'], relations: ['public.lead_mapping_profile'] },
  {
    key: ['lead-import', 'report'],
    relations: ['public.lead_import', 'public.lead_assignment', 'public.lead_verification_run', 'public.lead_purchase']
  },
  { key: ['lead-import', 'ref', 'sources'], relations: ['public.lead_source'] },
  { key: ['lead-import', 'ref', 'reps'], relations: ['public.rep'] },
  { key: ['merchant'], relations: ['public.merchant'] },

  // ACH
  { key: ['merchants_for_ach_intake'], relations: ['public.merchant'] },
  { key: ['ach_provider_config'], relations: ['public.ach_provider_config'] },
  {
    key: ['ach_mandates_for_merchant'],
    relations: [
      'public.ach_mandate',
      'public.ach_mandate_view',
      'public.ach_payment_method',
      'public.ach_customer'
    ]
  },

  // Files / docs
  { key: ['docs-crm-files'], relations: ['core_crm.file_object_link'] },
  { key: ['file-docs-row'], relations: ['core_crm.file_object_link'] },

  // Gmail integration
  { key: ['metadata', 'gmail'], relations: ['core_crm.gmail_mailbox', 'core_crm.gmail_provider'] },

  // Permissions / RBAC admin
  { key: ['my-capabilities'], relations: ['core_crm.user_capability'] },
  { key: ['app_group_member'], relations: ['core_crm.app_group_member'] },
  { key: ['rbac-capabilities'], relations: ['core_crm.capability'] },
  { key: ['rbac-group-capabilities'], relations: ['core_crm.group_capability'] },
  { key: ['rbac-group-scope'], relations: ['core_crm.app_group'] },
  {
    key: ['permission-matrix'],
    relations: ['core_crm.group_capability', 'core_crm.user_capability_override', 'core_crm.app_group_member', 'core_crm.capability']
  },
  {
    key: ['user-capability-overview'],
    relations: ['core_crm.group_capability', 'core_crm.user_capability_override', 'core_crm.app_group_member', 'core_crm.capability']
  },
  { key: ['permission-history'], relations: ['core_crm.group_capability', 'core_crm.user_capability_override'] },
  { key: ['ai-memory-app-users'], relations: ['core_crm.app_user'] }
];

/**
 * Custom query keys to invalidate given the set of relations affected by a change.
 */
export const getMatchingCustomQueryKeys = (
  affectedRelations: Set<QualifiedRelation>
): CustomQueryKeySource[] =>
  CUSTOM_QUERY_KEY_SOURCES.filter((source) =>
    source.relations.some((relation) => affectedRelations.has(relation))
  );
